import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UsePaginationOptions {
  defaultPageSize?: number;
  storageKey: string; // e.g., 'contracts-page-size', 'assessments-page-size'
}

interface UsePaginationReturn {
  currentPage: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  resetToFirstPage: () => void;
}

/**
 * Universal pagination hook with localStorage persistence and URL sync
 *
 * Features:
 * - Stores pageSize in localStorage per table/page
 * - Syncs currentPage and pageSize with URL params
 * - Provides helper to reset to first page
 *
 * @example
 * const { currentPage, pageSize, setCurrentPage, setPageSize } = usePagination({
 *   storageKey: 'contracts-page-size',
 *   defaultPageSize: 25
 * });
 */
export function usePagination(options: UsePaginationOptions): UsePaginationReturn {
  const { defaultPageSize = 25, storageKey } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Load pageSize from localStorage or use default
  const getInitialPageSize = useCallback((): number => {
    const urlPageSize = searchParams.get('limit');
    if (urlPageSize) {
      const parsed = Number.parseInt(urlPageSize, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const storedPageSize = localStorage.getItem(storageKey);
    if (storedPageSize) {
      const parsed = Number.parseInt(storedPageSize, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return defaultPageSize;
  }, [searchParams, storageKey, defaultPageSize]);

  // State
  const [currentPage, setCurrentPageState] = useState(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const parsed = Number.parseInt(pageParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 1) {
        return parsed;
      }
    }
    return 1;
  });

  const [pageSize, setPageSizeState] = useState(getInitialPageSize);

  // Save pageSize to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(storageKey, pageSize.toString());
  }, [pageSize, storageKey]);

  // Sync pagination state to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    } else {
      params.delete('page');
    }

    if (pageSize !== defaultPageSize) {
      params.set('limit', pageSize.toString());
    } else {
      params.delete('limit');
    }

    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, defaultPageSize, searchParams, setSearchParams]);

  // Wrapper for setCurrentPage
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  // Wrapper for setPageSize that resets to page 1
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setCurrentPageState(1); // Reset to first page when changing page size
  }, []);

  // Helper to reset to first page
  const resetToFirstPage = useCallback(() => {
    setCurrentPageState(1);
  }, []);

  return {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    resetToFirstPage
  };
}
